import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Car, Check, ArrowUpRight, Phone } from "lucide-react";
import { VEHICLES, TIER_INFO } from "../../data/vehicles";
import { TOWING_PROMO, money } from "../../data/promo";
import { SITE } from "../../site.config";

const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

export default function VehicleTierLookup({ onTier }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);

  const matches = useMemo(() => {
    const n = norm(q);
    if (n.length < 2) return [];
    const words = n.split(" ");
    return VEHICLES.filter((v) => {
      const hay = norm(`${v.make} ${v.model} ${v.years}`);
      return words.every((w) => hay.includes(w));
    }).slice(0, 8);
  }, [q]);

  const choose = (v) => {
    setPicked(v);
    setQ(`${v.make} ${v.model}`);
    if (onTier && v.tier !== "confirm") onTier(v.tier);
  };

  const info = picked ? TIER_INFO[picked.tier] : null;
  const towProPrice = picked && picked.tier !== "confirm" ? TOWING_PROMO.parts.towpro[picked.tier] : null;

  return (
    <div data-testid="vehicle-tier-lookup" className="border border-cyan-pz/40 bg-[#0A0A0A] p-6 md:p-8">
      <div className="flex items-center gap-3 mb-2">
        <Car className="w-5 h-5 text-cyan-pz" />
        <p className="bebas text-cyan-pz tracking-[0.3em] text-xs">NOT SURE WHICH TOW-PRO TIER YOU NEED?</p>
      </div>
      <h3 className="bebas text-white text-3xl md:text-4xl leading-tight">TYPE YOUR MAKE &amp; MODEL.</h3>

      <div className="relative mt-5 max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
        <input
          data-testid="vehicle-lookup-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPicked(null); }}
          placeholder="e.g. Ranger, LandCruiser 300, D-MAX 2022"
          autoComplete="off"
          className="w-full bg-black border border-white/15 focus:border-cyan-pz outline-none text-white barlow pl-12 pr-4 py-4 text-base placeholder:text-white/30 transition-colors"
        />
        {!picked && matches.length > 0 && (
          <ul data-testid="vehicle-lookup-results" className="absolute z-20 left-0 right-0 mt-1 bg-black border border-cyan-pz/40 divide-y divide-white/10 max-h-80 overflow-y-auto">
            {matches.map((v) => (
              <li key={`${v.make}-${v.model}`}>
                <button
                  type="button"
                  onClick={() => choose(v)}
                  data-testid={`vehicle-option-${norm(v.make + " " + v.model).replace(/ /g, "-")}`}
                  className="w-full text-left px-4 py-3 hover:bg-cyan-pz/10 transition-colors flex items-center justify-between gap-4"
                >
                  <span className="barlow text-white text-sm md:text-base">{v.make} <span className="text-white/90">{v.model}</span> <span className="text-white/40">({v.years})</span></span>
                  <span className="bebas text-cyan-pz tracking-widest text-xs whitespace-nowrap">{TIER_INFO[v.tier].short.toUpperCase()}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {!picked && q.trim().length >= 2 && matches.length === 0 && (
          <p data-testid="vehicle-lookup-none" className="mt-3 text-white/60 barlow text-sm">
            Not in our quick guide yet — call {SITE.business.phone} or hit Get a Quote with your make, model and year and we'll confirm your tier the same day.
          </p>
        )}
      </div>

      {picked && info && (
        <div data-testid="vehicle-lookup-result" className="mt-6 border border-white/10 bg-black p-5 md:p-6">
          <p className="bebas text-white/50 tracking-[0.25em] text-xs mb-1">{picked.make.toUpperCase()} {picked.model.toUpperCase()} · {picked.years}</p>
          <p data-testid="vehicle-lookup-tier" className="bebas text-cyan-pz text-2xl md:text-3xl leading-tight">{info.label}</p>
          <p className="mt-2 text-white/70 barlow text-sm md:text-base leading-relaxed">{info.blurb}</p>
          {towProPrice != null && (
            <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-2">
              <p className="barlow text-white/80 text-sm flex items-center gap-2"><Check className="w-4 h-4 text-cyan-pz" /> Tow-Pro Elite on its own: <span className="bebas text-cyan-pz text-xl">{picked.tier === "nextgen" ? "" : "from "}{money(towProPrice)}</span></p>
              <p className="barlow text-white/80 text-sm flex items-center gap-2"><Check className="w-4 h-4 text-cyan-pz" /> Packages for your tier are shown below</p>
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-3">
            {picked.tier === "confirm" ? (
              <a href={`tel:${SITE.business.phoneTel}`} data-testid="vehicle-lookup-call" className="inline-flex items-center gap-2 bg-cyan-pz text-black px-5 py-3 bebas tracking-[0.2em] text-base hover:bg-white transition-colors clip-perf"><Phone className="w-4 h-4" /> CALL TO CONFIRM</a>
            ) : (
              <a href="#compare" data-testid="vehicle-lookup-packages" className="inline-flex items-center gap-2 bg-cyan-pz text-black px-5 py-3 bebas tracking-[0.2em] text-base hover:bg-white transition-colors clip-perf">SEE MY PACKAGE PRICES <ArrowUpRight className="w-4 h-4" /></a>
            )}
            <Link to="/services/redarc-brake-controllers" className="inline-flex items-center gap-2 border border-white/20 text-white/80 px-5 py-3 bebas tracking-[0.2em] text-base hover:border-cyan-pz hover:text-cyan-pz transition-colors">TOW-PRO DETAILS</Link>
          </div>
        </div>
      )}
      <p className="mt-4 text-white/35 barlow text-xs">Guide only — we confirm the tier for your exact year and variant against REDARC's fitment guide before quoting.</p>
    </div>
  );
}
